import appStore                        from '../../store/AppStore';
import LsdApiModel                     from '../global/LsdApiModel';
import ProjectPoleModel                from '../ProjectPoleModel';
import ProjectCategoryModel            from '../ProjectCategoryModel';
import ProjectSubCategoryModel         from '../ProjectSubCategoryModel';
import { ExpenseStatus }               from 'constants/ExpenseStatus';
import { ExpensePaymentStatus }        from 'constants/ExpensePaymentStatus';
import { ExpensePurchaseOrderStatus }  from 'constants/ExpensePurchaseOrderStatus';
import { ExpensePaymentVoucherStatus } from 'constants/ExpensePaymentVoucherStatus';
import { computed }                    from 'mobx';
import ProjectModel                    from 'models/ProjectModel';

export type Budget = { amount: number, nbItems: number };
export type ExpenseBudgets = Record<ExpenseStatus, Budget>;
export type PaymentStatusBudgets = Record<ExpensePaymentStatus, Budget>;
export type PurchaseOrderBudgets = Record<ExpensePurchaseOrderStatus, Budget>;
export type PaymentVoucherBudgets = Record<ExpensePaymentVoucherStatus, Budget>;

type Constructor<T> = new (...args: any[]) => T;

export interface IBudgetAggregate {
	expenseBudgets: ExpenseBudgets;
	paymentStatusBudgets: PaymentStatusBudgets;
	purchaseOrderBudgets: PurchaseOrderBudgets;
	paymentVoucherBudgets: PaymentVoucherBudgets;
	budgetToValidateAmount: number;
	budgetToValidateNbItems: number;
	budgetValidatedAmount: number;
	budgetValidatedNbItems: number;
	budgetEstimatedAmount: number;
	budgetEstimatedNbItems: number;
	budgetPaidAmount: number;
	budgetPaidNbItems: number;
	budgetToPayAmount: number;
	budgetToPayNbItems: number;
	budgetRealAmount: number;
	budgetRealNbItems: number;
	estimatedBudget: number;
	nbItemsEstimatedBudget: number;
	realBudget: number;
	nbItemsRealBudget: number;
	targetBudget: number | null;
	isExceededBudgetTarget: boolean;
}

const BudgetAggregate = <TBase extends Constructor<any>>(Base: TBase) => {
	class BudgetAggregateModel extends Base implements IBudgetAggregate {

		@computed
		public get budgetToValidateAmount() {
			return parseFloat(this.get('budgetAggregate.budgetToValidateAmount', '0'));
		}

		@computed
		public get budgetToValidateNbItems() {
			return this.get('budgetAggregate.budgetToValidateNbItems', 0);
		}

		@computed
		public get budgetValidatedAmount() {
			return parseFloat(this.get('budgetAggregate.budgetValidatedAmount', '0'));
		}

		@computed
		public get budgetValidatedNbItems() {
			return this.get('budgetAggregate.budgetValidatedNbItems', 0);
		}

		@computed
		public get budgetEstimatedAmount() {
			return parseFloat(this.get('budgetAggregate.budgetEstimatedAmount', '0'));
		}

		@computed
		public get budgetEstimatedNbItems() {
			return this.get('budgetAggregate.budgetEstimatedNbItems', 0);
		}

		@computed
		public get budgetPaidAmount() {
			return parseFloat(this.get('budgetAggregate.budgetPaidAmount', '0'));
		}

		@computed
		public get budgetPaidNbItems() {
			return this.get('budgetAggregate.budgetPaidNbItems', 0);
		}

		@computed
		public get budgetToPayAmount() {
			return parseFloat(this.get('budgetAggregate.budgetToPayAmount', 0));
		}

		@computed
		public get budgetToPayNbItems() {
			return this.get('budgetAggregate.budgetToPayNbItems', 0);
		}

		@computed
		public get budgetRealAmount() {
			return parseFloat(this.get('budgetAggregate.budgetRealAmount', 0));
		}

		@computed
		public get budgetRealNbItems() {
			return this.get('budgetAggregate.budgetRealNbItems', 0);
		}

		// new methods

		@computed
		public get expenseBudgets(): ExpenseBudgets {
			return this.get('budgetAggregate.expenseBudgets', {});
		}

		@computed
		public get paymentStatusBudgets(): PaymentStatusBudgets {
			return this.get('budgetAggregate.paymentStatusBudgets', {});
		}

		@computed
		public get purchaseOrderBudgets(): PurchaseOrderBudgets {
			return this.get('budgetAggregate.purchaseOrderBudgets', {});
		}

		@computed
		public get paymentVoucherBudgets(): PaymentVoucherBudgets {
			return this.get('budgetAggregate.paymentVoucherBudgets', {});
		}

		// old methods reviewed

		@computed
		public get estimatedBudget(): number {
			return this.budgetToValidateAmount + this.budgetValidatedAmount;
		}

		@computed
		public get nbItemsEstimatedBudget(): number {
			return this.budgetToValidateNbItems + this.budgetValidatedNbItems;
		}

		@computed
		public get realBudget(): number {
			return this.budgetToPayAmount + this.budgetPaidAmount;
		}

		@computed
		public get nbItemsRealBudget(): number {
			return this.budgetToPayNbItems + this.budgetPaidNbItems;
		}

		@computed
		public get targetBudget(): number | null {
			return this.getTargetBudget(appStore.currentExpenseGroupId);
		}

		@computed
		public get isExceededBudgetTarget(): boolean {
			return this.estimatedBudget > this.targetBudget;
		}

		@computed
		public get isExceededBudgetTargetReal(): boolean {
			return this.realBudget > this.targetBudget;
		}

		@computed
		public get defaultModel(): LsdApiModel | null {
			const dictionary = {
				ProjectPole: ProjectPoleModel,
				ProjectCategory: ProjectCategoryModel,
				ProjectSubCategory: ProjectSubCategoryModel,
				Project: ProjectModel,
			};

			if (dictionary[this.get('@type')]) {
				return new dictionary[this.get('@type')]({
					...this.attributes,
				});
			}

			return null;
		}

		public getTargetBudget(expenseGroupId: id): number | null {

			const aggregate = this.get('budgetAggregate');
			if (!aggregate) {
				return null;
			}

			const targets = aggregate.budgetTargets;

			if (Array.isArray(targets)) {
				return null;
			}

			const target = targets && targets[expenseGroupId];
			return target ? target.amount : null;

			// to avoid troubles with:
			// return this.get(`budgetAggregate.budgetTargets.${expenseGroupId}.amount`, null);
		}
	}
	return BudgetAggregateModel;
};

export default BudgetAggregate;
